import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
import { I18nProvider } from './i18n';
import { ThemeNbProvider } from './context/ThemeNbContext';
import { ToastProvider } from './components/ToastNB';
import App from './App';
import SupportPage from './pages/SupportPage';
import ContactUsPage from './pages/ContactUsPage';
import TrackRepairPage from './pages/TrackRepairPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import TermsPageEn from './pages/en/terms';
import TermsPageEs from './pages/es/terms';
import TermsPageFr from './pages/fr/terms';
import PrivacyPageEn from './pages/en/privacy';
import PrivacyPageEs from './pages/es/privacy';
import PrivacyPageFr from './pages/fr/privacy';
import RoleGuard from './components/RoleGuard';
import TopNavLayout from './components/TopNavLayout';
import DashboardHome from './pages/admin/DashboardHome';
import { BranchList, BranchDetail, CreateBranch, EditBranch } from './pages/admin/branches';
import { UserList, InviteUser, UserDetail } from './pages/admin/users';
import { ReportsHome, ReportDetail } from './pages/admin/reports';
import ConfigHome from './pages/admin/config/ConfigHome';
import { EquipmentTypeList } from './pages/admin/equipment-types';
import OrdersList from './pages/admin/orders/OrdersList';
import OrderDetail from './pages/admin/orders/OrderDetail';
import SparePartsList from './pages/admin/spare-parts/SparePartsList';
import SparePartDetail from './pages/admin/spare-parts/SparePartDetail';
import TechnicianList from './pages/admin/technicians/TechnicianList';
import AlertsList from './pages/admin/alerts/AlertsList';
import TechnicianHome from './pages/technician/TechnicianHome';
import ReceiveEquipment from './pages/technician/ReceiveEquipment';
import EquipmentDetail from './pages/technician/EquipmentDetail';
import DeliverEquipment from './pages/technician/DeliverEquipment';
import ClientEquipmentDetail from './pages/client/EquipmentDetail';
import ApprovePart from './pages/client/ApprovePart';
import VerifyDelivery from './pages/client/VerifyDelivery';
import CapturePage from './pages/capture';
import AuthCallback from './pages/AuthCallback';
import AuthWebCallback from './pages/AuthWebCallback';
import RoleSelectionPage from './pages/setup/RoleSelectionPage';
import CreateOrganizationPage from './pages/setup/CreateOrganizationPage';
import JoinOrganizationPage from './pages/setup/JoinOrganizationPage';
import './index.css';

// RootRedirect reads language directly from localStorage to avoid async issues
function RootRedirect() {
  const getInitialLang = () => {
    try {
      // Check i18n store
      const storedLang = localStorage.getItem('tellowup-lang');
      if (storedLang && ['en', 'es', 'fr'].includes(storedLang)) {
        return storedLang;
      }
    } catch {}
    
    // Fallback to browser language
    const browserLang = navigator.language.split('-')[0];
    return ['en', 'es', 'fr'].includes(browserLang) ? browserLang : 'en';
  };
  
  const finalLang = getInitialLang();
  window.location.href = `/${finalLang}`;
  return null;
}

function TopNavRoute({ children }: { children: React.ReactNode }) {
  return (
    <TopNavLayout>
      <RoleGuard requiredRole="admin" fallback="redirect">
        {children}
      </RoleGuard>
    </TopNavLayout>
  );
}

function TechRoute({ children }: { children: React.ReactNode }) {
  return (
    <TopNavLayout>
      <RoleGuard requiredRole={['admin', 'technician']} fallback="redirect">
        {children}
      </RoleGuard>
    </TopNavLayout>
  );
}

// Layout component that syncs language from URL synchronously
function LangLayout({ children }: { children?: React.ReactNode }) {
  const { lang } = useParams();
  const forceLang = lang && ['en', 'es', 'fr'].includes(lang)
    ? (lang as 'en' | 'es' | 'fr')
    : undefined;

  return (
    <ThemeNbProvider>
      <I18nProvider forceLang={forceLang}>
        <ToastProvider>
          {children}
        </ToastProvider>
      </I18nProvider>
    </ThemeNbProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <LangLayout>
        <Routes>
          <Route path="/" element={<RootRedirect />} />

          {/* Lang-aware routes */}
          <Route path="/:lang" element={<App />} />
          <Route path="/:lang/login" element={<LoginPage />} />
          <Route path="/:lang/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/callback" element={<AuthWebCallback />} />
          <Route path="/auth" element={<AuthWebCallback />} />
          <Route path="/" element={<AuthWebCallback />} />
          <Route path="/:lang/register" element={<RegisterPage />} />
          
          {/* Setup flow - role selection & organization setup */}
          <Route path="/:lang/setup/role" element={<RoleSelectionPage />} />
          <Route path="/:lang/setup/create-org" element={<CreateOrganizationPage />} />
          <Route path="/:lang/join/:code" element={<JoinOrganizationPage />} />
          <Route path="/:lang/setup/join-org" element={<JoinOrganizationPage />} />
          
          <Route path="/:lang/support" element={<SupportPage />} />
          <Route path="/:lang/contact" element={<ContactUsPage />} />
          <Route path="/:lang/track" element={<TrackRepairPage />} />

          {/* Client routes */}
          <Route path="/:lang/client/track" element={<TrackRepairPage />} />
          <Route path="/:lang/client/equipment/:code" element={<ClientEquipmentDetail />} />
          <Route path="/:lang/client/approve-part/:id" element={<ApprovePart />} />
          <Route path="/:lang/verify/:order_id" element={<VerifyDelivery />} />

          {/* Phone photo capture (public, no auth) */}
          <Route path="/:lang/capture/:token" element={<CapturePage />} />

          {/* Admin routes */}
          <Route path="/:lang/admin" element={<TopNavRoute><DashboardHome /></TopNavRoute>} />
          <Route path="/:lang/admin/branches" element={<TopNavRoute><BranchList /></TopNavRoute>} />
          <Route path="/:lang/admin/branches/new" element={<TopNavRoute><CreateBranch /></TopNavRoute>} />
          <Route path="/:lang/admin/branches/:id/edit" element={<TopNavRoute><EditBranch /></TopNavRoute>} />
          <Route path="/:lang/admin/branches/:id" element={<TopNavRoute><BranchDetail /></TopNavRoute>} />
          <Route path="/:lang/admin/users" element={<TopNavRoute><UserList /></TopNavRoute>} />
          <Route path="/:lang/admin/users/invite" element={<TopNavRoute><InviteUser /></TopNavRoute>} />
          <Route path="/:lang/admin/users/:id" element={<TopNavRoute><UserDetail /></TopNavRoute>} />
          <Route path="/:lang/admin/reports" element={<TopNavRoute><ReportsHome /></TopNavRoute>} />
          <Route path="/:lang/admin/reports/:type" element={<TopNavRoute><ReportDetail /></TopNavRoute>} />
          <Route path="/:lang/admin/config" element={<TopNavRoute><ConfigHome /></TopNavRoute>} />
          <Route path="/:lang/admin/equipment-types" element={<TopNavRoute><EquipmentTypeList /></TopNavRoute>} />
          <Route path="/:lang/admin/orders/:id" element={<TopNavRoute><OrderDetail /></TopNavRoute>} />
          <Route path="/:lang/admin/orders" element={<TopNavRoute><OrdersList /></TopNavRoute>} />
          <Route path="/:lang/admin/technicians" element={<TopNavRoute><TechnicianList /></TopNavRoute>} />
          <Route path="/:lang/admin/alerts" element={<TopNavRoute><AlertsList /></TopNavRoute>} />
          <Route path="/:lang/admin/spare-parts" element={<TechRoute><SparePartsList /></TechRoute>} />
          <Route path="/:lang/admin/spare-parts/:id" element={<TechRoute><SparePartDetail /></TechRoute>} />

          {/* Technician routes — accessible to admin and technician */}
          <Route path="/:lang/technician" element={<TechRoute><TechnicianHome /></TechRoute>} />
          <Route path="/:lang/technician/receive" element={<TechRoute><ReceiveEquipment /></TechRoute>} />
          <Route path="/:lang/technician/equipment/:id" element={<TechRoute><EquipmentDetail /></TechRoute>} />
          <Route path="/:lang/technician/deliver/:id" element={<TechRoute><DeliverEquipment /></TechRoute>} />

          {/* Legal Pages - explicit per-language files (only T&C and Privacy) */}
          <Route path="/en/terms" element={<TermsPageEn />} />
          <Route path="/en/privacy" element={<PrivacyPageEn />} />
          <Route path="/es/terms" element={<TermsPageEs />} />
          <Route path="/es/privacy" element={<PrivacyPageEs />} />
          <Route path="/fr/terms" element={<TermsPageFr />} />
          <Route path="/fr/privacy" element={<PrivacyPageFr />} />
        </Routes>
      </LangLayout>
    </BrowserRouter>
  </React.StrictMode>
);